import { type parseDocument } from 'htmlparser2';
import { z } from 'zod';

import type { ContentAnalysis } from './content-analysis';

export enum SeoScoreRules {
  H1 = 'h1',
  KeywordInH1 = 'keywordInH1',
  KeywordInH2 = 'keywordInH2',
  H2andH3 = 'h2andh3',
  KeywordInTitle = 'keywordInTitle',
  Title = 'title',
  Description = 'description',
  Content = 'content',
  InternalLinks = 'internalLinks',
  ExternalLinks = 'externalLinks',
  Strongs = 'strongs',
  Terms = 'terms',
}

export enum SeoScoreGroups {
  Headings = 'headings',
  Meta = 'meta',
  Content = 'content',
  Links = 'links',
}

export enum SeoScoreState {
  Success = 'success',
  Warning = 'warning',
  Failed = 'failed',
}

export type SeoScoreData = {
  title: string;
  description: string;
  website: string;
  bodyHTML: string;
  bodyText: string;
  bodyRaw: string;
  keywords: string[];

  semanticTerms?: ContentAnalysis['semanticTerms'];
  averageHeadingsPerPage?: ContentAnalysis['averageHeadingsPerPage'];
  averageWordsPerPage?: ContentAnalysis['averageWordsPerPage'];
};

export const seoScoreResultSchema = z.object({
  rule: z.enum([
    SeoScoreRules.H1,
    SeoScoreRules.KeywordInH1,
    SeoScoreRules.KeywordInH2,
    SeoScoreRules.KeywordInTitle,
    SeoScoreRules.H2andH3,
    SeoScoreRules.Title,
    SeoScoreRules.Description,
    SeoScoreRules.Content,
    SeoScoreRules.InternalLinks,
    SeoScoreRules.ExternalLinks,
    SeoScoreRules.Strongs,
    SeoScoreRules.Terms,
  ]),
  group: z.enum([
    SeoScoreGroups.Headings,
    SeoScoreGroups.Meta,
    SeoScoreGroups.Content,
    SeoScoreGroups.Links,
  ]),
  state: z.enum([SeoScoreState.Failed, SeoScoreState.Success, SeoScoreState.Warning]),
  messageCode: z.string(), // error codes will be set up here
  score: z.number(),
  minScore: z.number(), // when not used, this value will be -1000,
  maxScore: z.number(), // when not used, this value will be -1000,
  extraData: z.record(z.number()), // any extra information from the rule
});
export type SeoScoreResult = z.infer<typeof seoScoreResultSchema>;
export type SeoScoreInternalResult = Omit<Omit<SeoScoreResult, 'rule'>, 'group'>;

export const seoResSchema = z.object({
  total: z.number(),
  result: z.array(seoScoreResultSchema),
});
export type SeoResponse = z.infer<typeof seoResSchema>;

export type SeoScoreRuleData = Omit<SeoScoreData, 'body'> & {
  body: ReturnType<typeof parseDocument>;
};
export type SeoScoreRuleFn = (data: SeoScoreRuleData) => SeoScoreInternalResult;
